import React, { useEffect, useMemo, useRef, useState } from "react";
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { StudentsList } from './StudentsList';
import { StudentForm, StudentFormRow, StudentRowErrors } from './StudentForm';
import { Student } from './StudentListItem';
import type { Program, ProgramAssignment } from "../ProgramsManager";
import { generateUUID } from '../../../../../utils/uuid';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

type Mode = "list" | "view" | "create";

type StudentProgramDateField = 'theoretical_start_date' | 'theoretical_end_date' | 'exam_date';

export interface StudentWithPrograms extends Student {
    programs: ProgramAssignment[];
}

interface StudentsManagerProps {
    appliedPrograms?: ProgramAssignment[];
    availablePrograms?: Program[];
    onDataChange?: (data: StudentWithPrograms[]) => void;
    initialStudents?: StudentWithPrograms[];
}

interface ParsedStudent {
    "ФИО": string;
    "Email"?: string;
}

interface ParseStudentsTemplateResponse {
    students: ParsedStudent[];
}

const toIsoDate = (date: Date | null) => {
    if (!date) return undefined;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const isValidEmail = (email: string): boolean => {
    if (!email.trim()) return true;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
};

const createEmptyRow = (): StudentFormRow => ({
    id: generateUUID(),
    full_name: '',
    email: '',
});

export const StudentsManager: React.FC<StudentsManagerProps> = ({
    appliedPrograms,
    availablePrograms = [],
    onDataChange,
    initialStudents,
}) => {
    const [mode, setMode] = useState<Mode>("list");
    const [search, setSearch] = useState("");
    const [students, setStudents] = useState<Student[]>([]);
    const studentsRef = useRef<Student[]>([]);
    useEffect(() => {
        studentsRef.current = students;
    }, [students]);
    const [selected, setSelected] = useState<Student | null>(null);

    const [createRows, setCreateRows] = useState<StudentFormRow[]>([createEmptyRow()]);
    const [createErrors, setCreateErrors] = useState<Record<string, StudentRowErrors>>({});

    const [flash, setFlash] = useState<string>("");
    const [flashType, setFlashType] = useState<'success' | 'error'>('success');
    const [studentPrograms, setStudentPrograms] = useState<Record<string, ProgramAssignment[]>>({});
    const appliedProgramsRef = useRef<ProgramAssignment[] | undefined>(undefined);
    const [viewErrors, setViewErrors] = useState<{ email?: string }>({});
    const [isDownloading, setIsDownloading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (initialStudents) {
            const initialStudentList = initialStudents.map(({ programs, ...student }) => student);
            const initialStudentPrograms = initialStudents.reduce((acc, curr) => {
                acc[curr.id] = curr.programs;
                return acc;
            }, {} as Record<string, ProgramAssignment[]>);

            setStudents(initialStudentList);
            setStudentPrograms(initialStudentPrograms);
        }
    }, [initialStudents]);

    useEffect(() => {
        if (onDataChange) {
            const dataToEmit: StudentWithPrograms[] = students.map(student => ({
                ...student,
                programs: studentPrograms[student.id] || []
            }));
            onDataChange(dataToEmit);
        }
    }, [students, studentPrograms, onDataChange]);

    useEffect(() => {
        appliedProgramsRef.current = appliedPrograms;
        if (!appliedPrograms) return;
        setStudentPrograms(() => {
            const next: Record<string, ProgramAssignment[]> = {};
            studentsRef.current.forEach((student) => {
                next[student.id] = appliedPrograms.map((program) => ({ ...program }));
            });
            return next;
        });
    }, [appliedPrograms]);

    useEffect(() => {
        if (!appliedProgramsRef.current) return;
        setStudentPrograms((prev) => {
            const next = { ...prev };
            let changed = false;
            students.forEach((student) => {
                if (!next[student.id]) {
                    next[student.id] = appliedProgramsRef.current!.map((program) => ({ ...program }));
                    changed = true;
                }
            });
            Object.keys(next).forEach((studentId) => {
                if (!students.some((s) => s.id === studentId)) {
                    delete next[studentId];
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [students]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return students;
        return students.filter((s) =>
            [s.full_name, s.email]
                .filter(Boolean)
                .join(" ")
                .toLowerCase()
                .includes(q)
        );
    }, [search, students]);

    const showFlash = (message: string, type: 'success' | 'error' = 'success') => {
        setFlash(message);
        setFlashType(type);
        setTimeout(() => setFlash(""), 4000);
    };

    const openView = (student: Student) => {
        setSelected(student);
        setMode("view");
        setViewErrors({});
    };

    const openCreate = () => {
        setSelected(null);
        setCreateRows([createEmptyRow()]);
        setCreateErrors({});
        setMode("create");
    };

    const closePanel = () => {
        setMode("list");
        setSelected(null);
        setCreateErrors({});
        setViewErrors({});
    };

    const handleDownloadTemplate = async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            showFlash('Ошибка авторизации: токен не найден', 'error');
            return;
        }

        setIsDownloading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/v1/applications/students-template`, {
                method: 'GET',
                headers: {
                    'Authorization': token,
                },
            });

            if (!response.ok) {
                throw new Error('Ошибка при скачивании шаблона');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = 'students_template.xlsx';
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1].replace(/['"]/g, '');
                }
            }

            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            showFlash('Шаблон успешно скачан', 'success');
        } catch (error) {
            console.error('Ошибка при скачивании шаблона:', error);
            showFlash(error instanceof Error ? error.message : 'Ошибка при скачивании шаблона', 'error');
        } finally {
            setIsDownloading(false);
        }
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const token = localStorage.getItem('token');
        if (!token) {
            showFlash('Ошибка авторизации: токен не найден', 'error');
            e.target.value = '';
            return;
        }

        const allowedExtensions = ['.xlsx', '.xls'];
        const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

        if (!allowedExtensions.includes(fileExtension)) {
            showFlash('Пожалуйста, выберите файл Excel (.xlsx или .xls)', 'error');
            e.target.value = '';
            return;
        }

        setIsUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await fetch(`${API_BASE_URL}/api/v1/applications/parse-students-template`, {
                method: 'POST',
                headers: {
                    'Authorization': token,
                },
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || 'Ошибка при загрузке шаблона');
            }

            const result: ParseStudentsTemplateResponse = await response.json();

            if (!result.students || result.students.length === 0) {
                showFlash('Файл не содержит данных о студентах', 'error');
                return;
            }

            const newRows: StudentFormRow[] = result.students
                .map(student => ({
                    id: generateUUID(),
                    full_name: student["ФИО"]?.toString().trim() || '',
                    email: student["Email"]?.toString().trim() || '',
                }))
                .filter(row => row.full_name.length > 0 || row.email.length > 0);

            if (newRows.length === 0) {
                showFlash('Файл не содержит валидных данных (ни ФИО, ни Email)', 'error');
                return;
            }

            setSelected(null);
            setCreateRows(newRows);
            setCreateErrors({});
            setMode("create");

            showFlash(`Загружено студентов из шаблона: ${newRows.length}`, 'success');

        } catch (error) {
            console.error('Ошибка при загрузке шаблона:', error);
            showFlash(error instanceof Error ? error.message : 'Ошибка при загрузке шаблона', 'error');
        } finally {
            setIsUploading(false);
            e.target.value = '';
        }
    };

    const validateRow = (row: StudentFormRow): StudentRowErrors => {
        const errors: StudentRowErrors = {};
        if (!row.full_name.trim()) errors.full_name = "Укажите ФИО";
        if (row.email && !isValidEmail(row.email)) errors.email = "Некорректный email";
        return errors;
    };

    const handleRowChange = (rowId: string, field: 'full_name' | 'email', value: string) => {
        setCreateRows(prev =>
            prev.map(row =>
                row.id === rowId ? { ...row, [field]: value } : row
            )
        );

        setCreateErrors(prev => {
            const rowErrors = prev[rowId] || {};
            if (rowErrors[field]) {
                const newRowErrors = { ...rowErrors };
                delete newRowErrors[field];
                return { ...prev, [rowId]: newRowErrors };
            }
            return prev;
        });

        if (field === 'email' && value && !isValidEmail(value)) {
            setCreateErrors(prev => ({
                ...prev,
                [rowId]: { ...prev[rowId], email: 'Некорректный email' }
            }));
        }
    };

    const handleAddRow = () => {
        setCreateRows(prev => [...prev, createEmptyRow()]);
    };

    const handleRemoveRow = (rowId: string) => {
        setCreateRows(prev => prev.filter(row => row.id !== rowId));
        setCreateErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[rowId];
            return newErrors;
        });
    };

    const handleStudentUpdate = (studentId: string, field: 'full_name' | 'email', value: string) => {
        if (field === 'email') {
            if (value && !isValidEmail(value)) {
                setViewErrors({ email: 'Некорректный email' });
            } else {
                setViewErrors({});
            }
        }

        const newStudents = students.map(s =>
            s.id === studentId ? { ...s, [field]: value } : s
        );
        setStudents(newStudents);

        if (selected && selected.id === studentId) {
            setSelected(prevSelected => ({ ...prevSelected!, [field]: value }));
        }
    };

    const onSave = (e: React.FormEvent) => {
        e.preventDefault();

        const allErrors: Record<string, StudentRowErrors> = {};
        let hasErrors = false;

        createRows.forEach(row => {
            const rowErrors = validateRow(row);
            if (Object.keys(rowErrors).length > 0) {
                allErrors[row.id] = rowErrors;
                if (rowErrors.full_name && !row.full_name.trim() && !row.email.trim()) {
                    const hasFilledRows = createRows.some(r => r.full_name.trim());
                    if (hasFilledRows) {
                        delete allErrors[row.id];
                    } else {
                        hasErrors = true;
                    }
                } else if (rowErrors.email) {
                    hasErrors = true;
                } else if (rowErrors.full_name && row.email.trim()) {
                    hasErrors = true;
                }
            }
        });

        setCreateErrors(allErrors);
        if (hasErrors) return;

        const validRows = createRows.filter(row => row.full_name.trim());

        if (validRows.length === 0) {
            setCreateErrors({ [createRows[0].id]: { full_name: 'Укажите ФИО хотя бы одного студента' } });
            return;
        }

        const newStudents: Student[] = validRows.map(row => ({
            id: generateUUID(),
            full_name: row.full_name.trim(),
            email: row.email.trim() || "",
            birth_date: "",
            phone: "",
            address: "",
            position: "",
            snils: "",
            updated_at: new Date().toISOString(),
        }));

        setStudents(prev => [...newStudents, ...prev]);

        setStudentPrograms(prev => {
            const next = { ...prev };
            newStudents.forEach(student => {
                next[student.id] = appliedProgramsRef.current
                    ? appliedProgramsRef.current.map(program => ({ ...program }))
                    : [];
            });
            return next;
        });

        const message = newStudents.length === 1
            ? "Студент успешно создан"
            : `Создано студентов: ${newStudents.length}`;
        showFlash(message, 'success');

        if (newStudents.length === 1) {
            openView(newStudents[0]);
        } else {
            closePanel();
        }
    };

    const handleStudentDelete = (studentId: string) => {
        if (window.confirm("Вы уверены, что хотите удалить этого студента?")) {
            if (selected?.id === studentId) closePanel();
            setStudents(prev => prev.filter(s => s.id !== studentId));
            setStudentPrograms((prev) => {
                const next = { ...prev };
                delete next[studentId];
                return next;
            });
            showFlash("Студент удален", 'success');
        }
    };

    const handleProgramDateChange = (
        studentId: string,
        programId: string,
        field: StudentProgramDateField,
        date: Date | null
    ) => {
        setStudentPrograms((prev) => {
            const existing = prev[studentId] || [];
            const updated = existing.map((program) =>
                program.program.id === programId ? { ...program, [field]: toIsoDate(date) } : program
            );
            return { ...prev, [studentId]: updated };
        });
    };

    const handleProgramRemove = (studentId: string, programId: string) => {
        setStudentPrograms((prev) => {
            const existing = prev[studentId] || [];
            const updated = existing.filter((program) => program.program.id !== programId);
            return { ...prev, [studentId]: updated };
        });
    };

    const handleProgramAdd = (studentId: string, programId: string) => {
        const program = availablePrograms.find((p) => p.id === programId);
        if (!program) return;
        setStudentPrograms((prev) => {
            const existing = prev[studentId] || [];
            if (existing.some((item) => item.program.id === programId)) {
                return prev;
            }
            const updated = [...existing, { program }];
            return { ...prev, [studentId]: updated };
        });
    };

    const isPanelOpen = mode === "view" || mode === "create";

    return (
        <section className="bg-white border border-gray-200 rounded-lg shadow-sm w-full">
            <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 sm:p-5 border-b border-gray-200">
                <h1 className="text-lg font-medium text-gray-900">Студенты</h1>
                <div className="flex flex-wrap items-center gap-2">
                    {/* Скрытый input для загрузки файла */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleFileChange}
                        className="hidden"
                    />

                    {/* Кнопка "Скачать шаблон" */}
                    <button
                        type="button"
                        onClick={handleDownloadTemplate}
                        disabled={isDownloading}
                        className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <DownloadIcon fontSize="small" />
                        {isDownloading ? 'Загрузка...' : 'Скачать шаблон'}
                    </button>

                    {/* Кнопка "Загрузить шаблон" */}
                    <button
                        type="button"
                        onClick={handleUploadClick}
                        disabled={isUploading}
                        className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <UploadIcon fontSize="small" />
                        {isUploading ? 'Обработка...' : 'Загрузить шаблон'}
                    </button>

                    {/* Кнопка "Добавить студента" */}
                    <button
                        type="button"
                        onClick={openCreate}
                        className="inline-flex items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 focus:outline-none"
                    >
                        <AddIcon fontSize="small" /> Добавить студента
                    </button>
                </div>
            </header>

            <div className="p-4 sm:p-6">
                {flash && (
                    <div className={`mb-4 flex items-center gap-3 text-sm rounded-lg px-4 py-3 border ${flashType === 'success'
                            ? 'text-green-800 bg-green-100 border-green-200'
                            : 'text-red-800 bg-red-100 border-red-200'
                        }`}>
                        {flashType === 'success'
                            ? <CheckCircleOutlineIcon className="text-green-600" />
                            : <ErrorOutlineIcon className="text-red-600" />
                        }
                        {flash}
                    </div>
                )}

                <div className={`grid grid-cols-1 gap-8 transition-all duration-300 ease-in-out ${isPanelOpen ? 'lg:grid-cols-2' : 'lg:grid-cols-1'}`}>
                    {mode !== 'create' && (
                        <div>
                            <StudentsList
                                students={filtered}
                                search={search}
                                onSearchChange={setSearch}
                                onStudentClick={openView}
                                onStudentDelete={handleStudentDelete}
                                selectedStudentId={selected?.id}
                            />
                        </div>
                    )}

                    {isPanelOpen && (
                        <div className={`${mode === 'create' ? 'lg:col-span-2' : ''}`}>
                            <StudentForm
                                mode={mode}
                                student={selected}
                                createRows={createRows}
                                createErrors={createErrors}
                                onRowChange={handleRowChange}
                                onAddRow={handleAddRow}
                                onRemoveRow={handleRemoveRow}
                                onSubmit={onSave}
                                onClose={closePanel}
                                programs={selected ? studentPrograms[selected.id] || [] : []}
                                availablePrograms={availablePrograms}
                                onProgramAdd={
                                    selected ? (programId) => handleProgramAdd(selected.id, programId) : undefined
                                }
                                onProgramRemove={
                                    selected ? (programId) => handleProgramRemove(selected.id, programId) : undefined
                                }
                                onProgramDateChange={
                                    selected
                                        ? (programId, field, date) =>
                                            handleProgramDateChange(selected.id, programId, field, date)
                                        : undefined
                                }
                                onStudentChange={handleStudentUpdate}
                                viewErrors={viewErrors}
                            />
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};